<template>
  <div class="relative">
    <DsPolicyAuthorizedSkeleton v-if="loading" />

    <div v-else>
      <div class="w-full flex flex-col lg:flex-row justify-between items-start gap-4 mb-4 mt-4">
        <span
          @click="toggleInsert"
          class="flex items-center gap-2 cursor-pointer font-bold text-primary"
        >
          <v-icon class="share-icon" color="primary" :icon="mdiPlusCircle" size="32" />
          Inserir Médicos
        </span>
      </div>

      <div
        v-if="showInsert"
        class="w-full flex flex-col lg:flex-row justify-between items-start gap-4 mb-8"
      >
        <div class="w-full lg:w-4/5">
          <v-autocomplete
            @update:model-value="setUsers"
            @update:search="searchUsers"
            :items="doctorStore.listDoctorsCompany"
            :loading="loadingAuthorized"
            clearable
            chips
            multiple
            class="w-full"
            color="primary20"
            variant="outlined"
            density="comfortable"
          />
        </div>

        <div class="w-full lg:w-1/5">
          <v-select
            v-model="selectedStatus"
            :items="[
              { title: 'Totalmente autorizado', value: 'FULLY_AUTHORIZED' },
              { title: 'Parcialmente autorizado', value: 'PARTIALLY_AUTHORIZED' }
            ]"
            item-title="title"
            item-value="value"
            label="Situação"
            variant="outlined"
            density="comfortable"
            color="primary"
          />
        </div>

        <div class="w-full lg:w-1/5">
          <filled-button
            @click="submitUsers"
            :loading="loadingAuthorized"
            :text="`Inserir Médicos`"
            width="100%"
            height="56px"
          />
        </div>
      </div>

      <div class="w-full mt-4 overflow-y-auto overflow-x-hidden" style="grid-area: table">
        <template v-if="paginatedDoctors.length > 0">
          <div
            class="hidden md:grid lg:grid-cols-12 md:grid-cols-8 gap-2 w-full items-center text-left py-2 border-b font-semibold text-gray-700 text-xs md:text-sm"
          >
            <div class="lg:col-span-2 md:col-span-2">Nome</div>
            <div class="lg:col-span-1 md:col-span-1">CRM</div>
            <div class="lg:col-span-1 md:col-span-1">CRM-UF</div>
            <div class="lg:col-span-1 md:col-span-1">Situação</div>
            <div class="lg:col-span-1 md:col-span-1">Data de Inserção</div>
            <div class="lg:col-span-2 md:col-span-1">Inserido Por</div>
            <div class="lg:col-span-1 hidden lg:block">Promovido em</div>
            <div class="lg:col-span-2 hidden lg:block">Promovido Por</div>
            <div class="lg:col-span-1 md:col-span-1">Ações</div>
          </div>

          <TableAuthorized
            v-for="doc in paginatedDoctors"
            :key="doc.id"
            :data="{
              id: doc.id,
              name: doc.doctor.user.name,
              crm: doc.doctor.mainCrm.number,
              crmUf: doc.doctor.mainCrm.uf,
              status:
                doc.authorizationStatus === 'FULLY_AUTHORIZED'
                  ? 'Totalmente autorizado'
                  : doc.authorizationStatus === 'PARTIALLY_AUTHORIZED'
                    ? 'Parcialmente autorizado'
                    : '—',
              insertedAt: formatDateTimeAtBR(doc.createdAt),
              insertedBy: doc.createdBy.name,
              promotedAt:
                doc.authorizationStatus === 'FULLY_AUTHORIZED'
                  ? formatDateTimeAtBR(doc.promotedAt)
                  : '',
              promotedBy:
                doc.authorizationStatus === 'FULLY_AUTHORIZED' ? doc.promotedBy?.name || '' : ''
            }"
          />
        </template>

        <EmptyList v-else title="Nenhum médico autorizado encontrado" />
      </div>

      <v-pagination
        v-model="currentPage"
        :total-visible="5"
        :length="pageCount"
        density="comfortable"
        color="primary"
        @update:model-value="changePage"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue'

import { useDsPolicyStore } from '@/pinia-store/useDsPolicyStore'
import {
  useDoctorStore,
  DOCTOR_FETCH_ALL_LIMIT,
  DOCTORS_LOAD_ERROR
} from '@/pinia-store/useDoctorStore'
import { storeToRefs } from 'pinia'
import { useErrorToast } from '@/composables/useErrorToast'
import { useDoctorsLoadError } from '../useDoctorsLoadError'

import EmptyList from '@/components/EmptyList.vue'
import TableAuthorized from '@/components/designSystem/Table/TableAuthorizedDsPolicies.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'

import { mdiPlusCircle } from '@mdi/js'
import { formatDateTimeAtBR } from '@/utils/date'

import DsPolicyAuthorizedSkeleton from '@/components/designSystem/Skeleton/DsPolicyAuthorizedSkeleton.vue'

const { notifyError } = useErrorToast()
const loading = ref(false)
const loadingAuthorized = ref(false)
const currentPage = ref(1)
const itemsPerPage = ref(5)

const showInsert = ref(false)
const doctorStore = useDoctorStore()
const { doctorsCompany } = storeToRefs(doctorStore)
const selectedStatus = ref<'FULLY_AUTHORIZED' | 'PARTIALLY_AUTHORIZED' | ''>('')

const { notifyDoctorsLoadError } = useDoctorsLoadError()

onMounted(async () => {
  loading.value = true
  try {
    doctorStore.doctorQuery.limit = DOCTOR_FETCH_ALL_LIMIT
    await doctorStore.fetchDoctors()
    notifyDoctorsLoadError()
    // await policyStore.fetchAuthorizedDoctorsByDsPolicy(policyStore.currentPolicy!.id)
  } catch (error) {
    notifyError(error, DOCTORS_LOAD_ERROR)
  } finally {
    loading.value = false
  }
})

const toggleInsert = () => {
  showInsert.value = !showInsert.value
}

const selectedUsers = ref<string[]>([])

const emit = defineEmits(['update:users'])
const setUsers = (v: string[]) => {
  selectedUsers.value = v
  emit('update:users', v)
}

const searchUsers = async (search: string) => {
  try {
    loadingAuthorized.value = true
    await doctorStore.applyFilters({ search }, false)
    doctorsCompany.value = doctorStore.doctorsCompany
    notifyDoctorsLoadError()
  } catch (error) {
    notifyError(error, DOCTORS_LOAD_ERROR)
  } finally {
    loadingAuthorized.value = false
  }
}

const submitUsers = async () => {
  const policyId = dsPolicyStore.currentPolicy?.id

  // Guards locais (estado da tela, não erro de API) — ficam fora do notifyError.
  if (!policyId) {
    console.error('Policy ID não encontrado.')
    return
  }

  if (!selectedStatus.value) {
    console.error('Situação não selecionada.')
    return
  }

  const doctorIds = selectedUsers.value.map(Number)

  const payload = {
    dsPolicyId: policyId,
    doctorIds,
    authorizationStatus: selectedStatus.value
  }

  loading.value = true
  try {
    await dsPolicyStore.addAuthorizedDoctors(policyId, payload)
  } catch (error) {
    // Mantém a seleção e o formulário aberto para o usuário tentar de novo.
    notifyError(error, 'Não foi possível autorizar os médicos na política de restrição.')
    loading.value = false
    return
  }

  selectedUsers.value = []
  selectedStatus.value = ''
  showInsert.value = false

  try {
    await dsPolicyStore.fetchAuthorizedDoctorsByDsPolicy(policyId)
  } catch (error) {
    notifyError(error, 'Não foi possível atualizar a lista de médicos autorizados.')
  } finally {
    loading.value = false
  }
}

const dsPolicyStore = useDsPolicyStore()
const { authorizedDoctors } = storeToRefs(dsPolicyStore)

const paginatedDoctors = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return authorizedDoctors.value.slice(start, end)
})

const pageCount = computed(() => Math.ceil(authorizedDoctors.value.length / itemsPerPage.value))

const changePage = (page: number) => {
  currentPage.value = page
}
</script>
